import * as Sentry from "@sentry/react";

import { isProdEnvironment } from "../utils/environment";

const SENTRY_DSN = "https://14fb6ff7e746c3c48bd6e35c26f803b3@o4507565072973824.ingest.de.sentry.io/4511580399468624";

const reportedErrors = new WeakSet<object>();

const EXPECTED_ERROR_NAMES = new Set(["AbortError", "PaymentRequiredError", "QuotaExceededError", "ZodError"]);

const isObject = (value: unknown): value is object => typeof value === "object" && value !== null;

const getErrorName = (error: unknown): string | undefined => {
  if (!isObject(error) || !("name" in error)) return undefined;

  const name = (error as { name?: unknown }).name;
  return typeof name === "string" ? name : undefined;
};

export const getErrorStatus = (error: unknown): number | undefined => {
  if (typeof error === "number") return error;
  if (typeof Response !== "undefined" && error instanceof Response) return error.status;
  if (!isObject(error) || !("status" in error)) return undefined;

  const status = (error as { status?: unknown }).status;
  return typeof status === "number" ? status : undefined;
};

export const shouldCaptureHandledException = (error: unknown): boolean => {
  const status = getErrorStatus(error);
  if (status !== undefined) return false;

  const errorName = getErrorName(error);
  if (errorName && EXPECTED_ERROR_NAMES.has(errorName)) return false;

  return true;
};

const markReported = (error: unknown): void => {
  if (isObject(error)) {
    reportedErrors.add(error);
  }
};

const wasAlreadyReported = (error: unknown): boolean => isObject(error) && reportedErrors.has(error);

/**
 * OAuth/OIDC parameters that Keycloak puts into the redirect URL (fragment for the standard flow, `#access_token` for
 * the old implicit flow, query for response_mode=query). Sentry starts before initAuth strips them from the URL and
 * runs with sendDefaultPii, so request URLs, Referer headers and navigation breadcrumbs could carry them.
 */
const AUTH_URL_PARAMS = /([?#&])(access_token|id_token|refresh_token|code|state|session_state|iss)=[^&#\s"']*/gi;

export const scrubAuthParams = (value: string): string => value.replace(AUTH_URL_PARAMS, "$1$2=[Filtered]");

const scrubValue = (value: unknown): unknown => {
  if (typeof value === "string") return scrubAuthParams(value);
  if (Array.isArray(value)) return value.map(scrubValue);
  if (isObject(value)) {
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, scrubValue(item)]));
  }
  return value;
};

export const scrubBreadcrumb = (breadcrumb: Sentry.Breadcrumb): Sentry.Breadcrumb => ({
  ...breadcrumb,
  ...(breadcrumb.message !== undefined && { message: scrubAuthParams(breadcrumb.message) }),
  ...(breadcrumb.data && { data: scrubValue(breadcrumb.data) as Sentry.Breadcrumb["data"] }),
});

export const scrubEvent = <T extends Sentry.Event>(event: T): T => {
  const scrubbed: T = { ...event };
  if (event.request) {
    scrubbed.request = scrubValue(event.request) as Sentry.Event["request"];
  }
  if (event.breadcrumbs) {
    scrubbed.breadcrumbs = event.breadcrumbs.map(scrubBreadcrumb);
  }
  if (event.transaction) {
    scrubbed.transaction = scrubAuthParams(event.transaction);
  }
  return scrubbed;
};

export const initSentry = (): void => {
  // Only enable Sentry in production builds to avoid noise from local/Staging errors.
  if (!isProdEnvironment()) {
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: process.env.NODE_ENV,
    // Capture 10% of transactions for performance monitoring.
    tracesSampleRate: 0.1,
    sendDefaultPii: true,
    beforeBreadcrumb: (breadcrumb) => scrubBreadcrumb(breadcrumb),
    beforeSend: (event) => scrubEvent(event),
    beforeSendTransaction: (event) => scrubEvent(event),
  });
};

/**
 * Report a handled error to Sentry. No-op outside production (Sentry isn't
 * initialized there). Use for client-only failures the server can't see —
 * network errors, unparseable responses — not for HTTP errors that already
 * reached (and can be logged by) the server.
 */
export const captureException = (error: unknown, context?: Record<string, unknown>): void => {
  markReported(error);
  Sentry.captureException(error, context ? { extra: context } : undefined);
};

/**
 * Report a handled error that was shown to the user and swallowed by UI code.
 * HTTP/API responses are skipped because the API owns those logs.
 */
export const captureHandledException = (error: unknown, context?: Record<string, unknown>): void => {
  if (!shouldCaptureHandledException(error) || wasAlreadyReported(error)) {
    return;
  }

  captureException(error, {
    handled: true,
    status: getErrorStatus(error),
    ...context,
  });
};

export { Sentry };
